import React, { useContext, useMemo } from 'react';
import { Grid, Tooltip } from '@material-ui/core';
import { css } from '@emotion/react';
import { useClientUser } from '@/hooks/useClientUser';

import StickyFooter from '../../StickyFooter';
import { Context } from '../store';
import { SHIPMENT_FORM_STATE } from '../constants';

import { PrimaryButton, SecondaryButton } from '@/styles/blocks';

// MUI v4 Tooltips don't fire on disabled buttons (the button doesn't dispatch
// events). Wrapping in a span gives the tooltip a hoverable target, so the user
// gets an explanation when Price & Review / Send to Marketplace / Send to OMS
// are greyed out. The wrapper is always rendered (and the listeners just
// toggled) to avoid React unmounting/remounting the button when the hint
// transitions null ↔ truthy, which would cause focus loss and animation
// glitches mid-edit.
const DisabledHint = ({ hint, children }) => (
    <Tooltip
        title={hint || ''}
        arrow
        disableHoverListener={!hint}
        disableFocusListener={!hint}
        disableTouchListener={!hint}
    >
        <span>{children}</span>
    </Tooltip>
);

export default function Footer() {
    const { state, callbacks, errors } = useContext(Context);
    const { circles } = useClientUser();

    const blockedOrderCount = useMemo(() => {
        return Object.values(errors.missingFieldsByOrder || {}).filter((tabs) =>
            Object.values(tabs || {}).some((x) => x)
        ).length;
    }, [errors.missingFieldsByOrder]);

    const isValid = useMemo(() => {
        return Object.keys(state.orders).length > 0 && blockedOrderCount === 0;
    }, [state.orders, blockedOrderCount]);

    // Build a tooltip explaining every reason a submit button is disabled. Returns
    // null while the form is loading or submitting (the button is transiently
    // disabled, an explanation would be noisy).
    const hintForButton = (forOms) => {
        if (state.formState !== SHIPMENT_FORM_STATE.REVIEW || state.isSubmitting) return null;
        const reasons = [];
        if (Object.keys(state.orders).length === 0) {
            reasons.push('Add at least one order before submitting.');
        } else if (blockedOrderCount > 0) {
            reasons.push(
                `${blockedOrderCount} order${blockedOrderCount === 1 ? '' : 's'} ${
                    blockedOrderCount === 1 ? 'has' : 'have'
                } missing required fields. Click any "Missing required fields" link on a card to fix.`
            );
        }
        if (
            forOms &&
            (!!state?.pickupInfo?.is_middle_mile || !!state?.pickupInfo?.is_middle_mile_origin)
        ) {
            reasons.push("This is a middle-mile pickup and can't be sent to OMS.");
        }
        return reasons.length > 0 ? reasons.join(' ') : null;
    };

    const sharedHint = hintForButton(false);
    const omsHint = hintForButton(true);

    return (
        <StickyFooter
            styles={{
                margin: 0,
                boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.25)',
            }}
        >
            <Grid
                container
                direction="row"
                css={css`
                    justify-content: space-between;
                `}
            >
                <Grid item>
                    <SecondaryButton
                        css={css`
                            margin-left: 15px;
                        `}
                        onClick={() => callbacks.reset()}
                        disabled={state.isSubmitting}
                    >
                        Cancel
                    </SecondaryButton>
                </Grid>

                <Grid item>
                    {circles?.['ltl-marketplace'] && !circles?.['saas-v1'] ? (
                        <DisabledHint hint={sharedHint}>
                            <PrimaryButton
                                css={css`
                                    width: 195px;
                                `}
                                onClick={() => callbacks.submit({ oms: false })}
                                disabled={
                                    state.formState !== SHIPMENT_FORM_STATE.REVIEW ||
                                    state.isSubmitting ||
                                    !isValid
                                }
                            >
                                Price & Review
                            </PrimaryButton>
                        </DisabledHint>
                    ) : null}
                    {circles?.['ltl-marketplace'] && circles?.['saas-v1'] ? (
                        <DisabledHint hint={sharedHint}>
                            <PrimaryButton
                                css={css`
                                    margin-right: 30px;
                                    width: 213px;
                                `}
                                onClick={() => callbacks.submit({ oms: false })}
                                disabled={
                                    state.formState !== SHIPMENT_FORM_STATE.REVIEW ||
                                    state.isSubmitting ||
                                    !isValid
                                }
                            >
                                Send to Marketplace
                            </PrimaryButton>
                        </DisabledHint>
                    ) : null}
                    {circles?.['saas-v1'] || circles?.['broker'] ? (
                        <DisabledHint hint={omsHint}>
                            <PrimaryButton
                                css={css`
                                    width: 195px;
                                `}
                                onClick={() => {
                                    callbacks.submit({ oms: true });
                                }}
                                disabled={
                                    state.formState !== SHIPMENT_FORM_STATE.REVIEW ||
                                    state.isSubmitting ||
                                    !!state?.pickupInfo?.is_middle_mile ||
                                    !!state?.pickupInfo?.is_middle_mile_origin ||
                                    !isValid
                                }
                            >
                                Send to OMS
                            </PrimaryButton>
                        </DisabledHint>
                    ) : null}
                </Grid>
            </Grid>
        </StickyFooter>
    );
}
